"use client";

import { c as _c } from "react/compiler-runtime";
import type { Editor } from "@tiptap/react";
import { useEditorState } from "@tiptap/react";
import React, { useCallback, useState } from "react";
import { LuPlus, LuX } from "react-icons/lu";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cx } from "@/lib/tailwind-utils";
import { readStyle, writeStyle } from "./blocks/shared-block-styles";
import type { EmailBlockDefinition } from "./blocks/types";
import type { ActiveBlock } from "./context";

/* ── Shared helpers ───────────────────────────────────────────────────── */

function Section(t0) {
  const $ = _c(5);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-caption-emphasis uppercase tracking-wider text-foreground-muted">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="space-y-3 px-4 py-3">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function SelectControl(t0) {
  const $ = _c(14);
  const {
    label,
    value,
    options,
    onChange
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Label className="text-caption">{label}</Label>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onChange) {
    t2 = e => onChange(e.target.value);
    $[2] = onChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cx("h-8 w-full rounded-md border border-border bg-background px-2 text-caption text-foreground", "focus:outline-none focus:ring-2 focus:ring-accent-500/40");
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== options) {
    t4 = options.map(_temp);
    $[5] = options;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4 || $[9] !== value) {
    t5 = <select value={value} onChange={t2} className={t3}>{t4}</select>;
    $[7] = t2;
    $[8] = t4;
    $[9] = value;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t5) {
    t6 = <div className="space-y-1">{t1}{t5}</div>;
    $[11] = t1;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
}
function _temp(opt) {
  return <option key={opt.value} value={opt.value}>{opt.label}</option>;
}
export function ColorControl(t0) {
  const $ = _c(22);
  const {
    label,
    value,
    onChange,
    onClear
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <Label className="text-caption">{label}</Label>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = value || "#ffffff";
  let t3;
  if ($[2] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[2] = onChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <input type="color" value={t2} onChange={t3} className="size-8 shrink-0 cursor-pointer rounded border border-border bg-transparent" />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onChange) {
    t5 = e_0 => onChange(e_0.target.value);
    $[7] = onChange;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5 || $[10] !== value) {
    t6 = <Input value={value} onChange={t5} className="h-8 font-mono text-caption" placeholder="#ffffff" />;
    $[9] = t5;
    $[10] = value;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onClear || $[13] !== value) {
    t7 = onClear && value && <button type="button" onClick={onClear} className="shrink-0 rounded p-1 text-foreground-secondary hover:bg-hover hover:text-foreground"><LuX className="size-3" /></button>;
    $[12] = onClear;
    $[13] = value;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t4 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className="flex items-center gap-2">{t4}{t6}{t7}</div>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t1 || $[20] !== t8) {
    t9 = <div className="space-y-1">{t1}{t8}</div>;
    $[19] = t1;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}

/* ── Text alignment ───────────────────────────────────────────────────── */

const TEXT_ALIGN_OPTIONS = [{
  label: "Left",
  value: "left"
}, {
  label: "Center",
  value: "center"
}, {
  label: "Right",
  value: "right"
}];

/* ── Block style controls (shared across text blocks) ─────────────────── */

const FONT_SIZE_OPTIONS = [{
  label: "Default",
  value: ""
}, {
  label: "12px",
  value: "12px"
}, {
  label: "14px",
  value: "14px"
}, {
  label: "16px",
  value: "16px"
}, {
  label: "18px",
  value: "18px"
}, {
  label: "22px",
  value: "22px"
}, {
  label: "28px",
  value: "28px"
}, {
  label: "36px",
  value: "36px"
}];
const FONT_WEIGHT_OPTIONS = [{
  label: "Default",
  value: ""
}, {
  label: "Normal",
  value: "400"
}, {
  label: "Medium",
  value: "500"
}, {
  label: "Semi-bold",
  value: "600"
}, {
  label: "Bold",
  value: "700"
}];
const LINE_HEIGHT_OPTIONS = [{
  label: "Default",
  value: ""
}, {
  label: "1.0",
  value: "1"
}, {
  label: "1.25",
  value: "1.25"
}, {
  label: "1.5",
  value: "1.5"
}, {
  label: "1.75",
  value: "1.75"
}, {
  label: "2.0",
  value: "2"
}];
const TEXT_DECORATION_OPTIONS = [{
  label: "Default",
  value: ""
}, {
  label: "None",
  value: "none"
}, {
  label: "Underline",
  value: "underline"
}, {
  label: "Line-through",
  value: "line-through"
}];
const SPACING_OPTIONS = [{
  label: "None",
  value: ""
}, {
  label: "0px",
  value: "0px"
}, {
  label: "4px",
  value: "4px"
}, {
  label: "8px",
  value: "8px"
}, {
  label: "12px",
  value: "12px"
}, {
  label: "16px",
  value: "16px"
}, {
  label: "24px",
  value: "24px"
}, {
  label: "32px",
  value: "32px"
}, {
  label: "48px",
  value: "48px"
}];
const BORDER_RADIUS_OPTIONS = [{
  label: "None",
  value: ""
}, {
  label: "0px",
  value: "0px"
}, {
  label: "4px",
  value: "4px"
}, {
  label: "8px",
  value: "8px"
}, {
  label: "12px",
  value: "12px"
}, {
  label: "16px",
  value: "16px"
}, {
  label: "9999px",
  value: "9999px"
}];
const BORDER_WIDTH_OPTIONS = [{
  label: "None",
  value: ""
}, {
  label: "0px",
  value: "0px"
}, {
  label: "1px",
  value: "1px"
}, {
  label: "2px",
  value: "2px"
}, {
  label: "3px",
  value: "3px"
}, {
  label: "4px",
  value: "4px"
}];
const BORDER_STYLE_OPTIONS = [{
  label: "Default",
  value: ""
}, {
  label: "Solid",
  value: "solid"
}, {
  label: "Dashed",
  value: "dashed"
}, {
  label: "Dotted",
  value: "dotted"
}, {
  label: "None",
  value: "none"
}];

/** Style definitions with category grouping and control config. */
interface StyleDef {
  key: string;
  label: string;
  category: string;
  type: "select" | "color";
  options?: {
    label: string;
    value: string;
  }[];
  initial: string;
}
const ALL_ADDITIONAL_STYLES: StyleDef[] = [
// Typography
{
  key: "fontWeight",
  label: "Font weight",
  category: "Typography",
  type: "select",
  options: FONT_WEIGHT_OPTIONS,
  initial: "400"
}, {
  key: "lineHeight",
  label: "Line height",
  category: "Typography",
  type: "select",
  options: LINE_HEIGHT_OPTIONS,
  initial: "1.5"
}, {
  key: "textDecoration",
  label: "Text decoration",
  category: "Typography",
  type: "select",
  options: TEXT_DECORATION_OPTIONS,
  initial: "none"
},
// Appearance
{
  key: "borderRadius",
  label: "Border radius",
  category: "Appearance",
  type: "select",
  options: BORDER_RADIUS_OPTIONS,
  initial: "4px"
}, {
  key: "borderWidth",
  label: "Border width",
  category: "Appearance",
  type: "select",
  options: BORDER_WIDTH_OPTIONS,
  initial: "1px"
}, {
  key: "borderStyle",
  label: "Border style",
  category: "Appearance",
  type: "select",
  options: BORDER_STYLE_OPTIONS,
  initial: "solid"
}, {
  key: "borderColor",
  label: "Border color",
  category: "Appearance",
  type: "color",
  initial: "#e5e7eb"
},
// Spacing
{
  key: "paddingTop",
  label: "Padding top",
  category: "Spacing",
  type: "select",
  options: SPACING_OPTIONS,
  initial: "8px"
}, {
  key: "paddingBottom",
  label: "Padding bottom",
  category: "Spacing",
  type: "select",
  options: SPACING_OPTIONS,
  initial: "8px"
}, {
  key: "paddingLeft",
  label: "Padding left",
  category: "Spacing",
  type: "select",
  options: SPACING_OPTIONS,
  initial: "8px"
}, {
  key: "paddingRight",
  label: "Padding right",
  category: "Spacing",
  type: "select",
  options: SPACING_OPTIONS,
  initial: "8px"
}, {
  key: "marginTop",
  label: "Margin top",
  category: "Spacing",
  type: "select",
  options: SPACING_OPTIONS,
  initial: "0px"
}, {
  key: "marginBottom",
  label: "Margin bottom",
  category: "Spacing",
  type: "select",
  options: SPACING_OPTIONS,
  initial: "0px"
}];

/**
 * Every control here is a plain CSS property, so the edit goes into the block's
 * as-written style map — the same store a designed template lands in, which is
 * what keeps one value per property instead of two tiers arguing.
 */
function styleUpdater(attrs: Record<string, unknown>, onUpdate: (patch: Record<string, unknown>) => void) {
  return (key: string, value: string) => onUpdate(writeStyle(attrs, key, value));
}
function renderStyleControl(def: StyleDef, attrs: Record<string, unknown>, onUpdate: (patch: Record<string, unknown>) => void) {
  const setStyle = styleUpdater(attrs, onUpdate);
  const val = readStyle(attrs, def.key);
  if (def.type === "color") {
    return <ColorControl key={def.key} label={def.label} value={val} onChange={v => setStyle(def.key, v)} onClear={() => setStyle(def.key, "")} />;
  }
  return <SelectControl key={def.key} label={def.label} value={val} options={def.options!} onChange={v => setStyle(def.key, v)} />;
}
function BlockStyleControls({
  attrs,
  onUpdate
}: {
  attrs: Record<string, unknown>;
  onUpdate: (patch: Record<string, unknown>) => void;
}) {
  const [showAddMenu, setShowAddMenu] = useState(false);
  const setStyle = styleUpdater(attrs, onUpdate);
  const visibleAdditional: StyleDef[] = [];
  const hiddenAdditional: StyleDef[] = [];
  const hiddenByCategory: Record<string, StyleDef[]> = {};
  for (const s of ALL_ADDITIONAL_STYLES) {
    if (readStyle(attrs, s.key)) {
      visibleAdditional.push(s);
      continue;
    }
    hiddenAdditional.push(s);
    (hiddenByCategory[s.category] ??= []).push(s);
  }
  return <Section title="Styles">
      {/* Always visible */}
      <ColorControl label="Background" value={readStyle(attrs, "backgroundColor")} onChange={v => setStyle("backgroundColor", v)} onClear={() => setStyle("backgroundColor", "")} />
      <ColorControl label="Text color" value={readStyle(attrs, "color")} onChange={v_0 => setStyle("color", v_0)} onClear={() => setStyle("color", "")} />
      <SelectControl label="Font size" value={readStyle(attrs, "fontSize")} options={FONT_SIZE_OPTIONS} onChange={v_1 => setStyle("fontSize", v_1)} />

      {/* Dynamically shown additional styles */}
      {visibleAdditional.map(s_0 => renderStyleControl(s_0, attrs, onUpdate))}

      {/* Add more styles button */}
      {hiddenAdditional.length > 0 && <div className="relative">
          <button type="button" onClick={() => setShowAddMenu(!showAddMenu)} className="flex items-center gap-1 text-caption text-foreground-secondary hover:text-foreground">
            <LuPlus className="size-3" />
            Add style
          </button>

          {showAddMenu && <div className="absolute left-0 top-full z-50 mt-1 max-h-64 w-52 overflow-y-auto rounded-md border border-border bg-background py-1 shadow-lg">
              {Object.entries(hiddenByCategory).map(([category, styles]) => <div key={category}>
                  <div className="px-3 py-1.5 text-tiny font-semibold uppercase tracking-wider text-foreground-muted">
                    {category}
                  </div>
                  {styles.map(s_1 => <button key={s_1.key} type="button" className="w-full px-3 py-1.5 text-left text-caption text-foreground hover:bg-hover" onClick={() => {
            setStyle(s_1.key, s_1.initial);
            setShowAddMenu(false);
          }}>
                      {s_1.label}
                    </button>)}
                </div>)}
            </div>}
        </div>}
    </Section>;
}

/* ── Per-block controls ───────────────────────────────────────────────── */

function ParagraphControls(t0) {
  const $ = _c(11);
  const {
    attrs,
    onUpdate
  } = t0;
  const t1 = attrs.textAlign as string || "left";
  let t2;
  if ($[0] !== onUpdate) {
    t2 = v => onUpdate({
      textAlign: v
    });
    $[0] = onUpdate;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = <Section title="Paragraph"><SelectControl label="Text align" value={t1} options={TEXT_ALIGN_OPTIONS} onChange={t2} /></Section>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== attrs || $[6] !== onUpdate) {
    t4 = <BlockStyleControls attrs={attrs} onUpdate={onUpdate} />;
    $[5] = attrs;
    $[6] = onUpdate;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}
function HeadingControls(t0) {
  const $ = _c(20);
  const {
    attrs,
    onUpdate
  } = t0;
  const t1 = String(attrs.level ?? 1);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [{
      label: "H1",
      value: "1"
    }, {
      label: "H2",
      value: "2"
    }, {
      label: "H3",
      value: "3"
    }];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== onUpdate) {
    t3 = v => onUpdate({
      level: Number(v)
    });
    $[1] = onUpdate;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t3) {
    t4 = <SelectControl label="Level" value={t1} options={t2} onChange={t3} />;
    $[3] = t1;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = attrs.textAlign as string || "left";
  let t6;
  if ($[6] !== onUpdate) {
    t6 = v_0 => onUpdate({
      textAlign: v_0
    });
    $[6] = onUpdate;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t5 || $[9] !== t6) {
    t7 = <SelectControl label="Text align" value={t5} options={TEXT_ALIGN_OPTIONS} onChange={t6} />;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t4 || $[12] !== t7) {
    t8 = <Section title="Heading">{t4}{t7}</Section>;
    $[11] = t4;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== attrs || $[15] !== onUpdate) {
    t9 = <BlockStyleControls attrs={attrs} onUpdate={onUpdate} />;
    $[14] = attrs;
    $[15] = onUpdate;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
function ImageControls(t0) {
  const $ = _c(29);
  const {
    attrs,
    onUpdate
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Label className="text-caption">Alt text</Label>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = attrs.alt as string || "";
  let t3;
  if ($[1] !== onUpdate) {
    t3 = e => onUpdate({
      alt: e.target.value
    });
    $[1] = onUpdate;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = <div className="space-y-1">{t1}<Input value={t2} onChange={t3} className="h-8 text-caption" placeholder="Describe the image..." /></div>;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Label className="text-caption">URL</Label>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = attrs.href as string || "";
  let t7;
  if ($[7] !== onUpdate) {
    t7 = e_0 => onUpdate({
      href: e_0.target.value
    });
    $[7] = onUpdate;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <div className="space-y-1">{t5}<Input value={t6} onChange={t7} className="h-8 text-caption" placeholder="https://..." /></div>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = attrs.width as string || "100%";
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [{
      label: "Auto",
      value: "auto"
    }, {
      label: "25%",
      value: "25%"
    }, {
      label: "50%",
      value: "50%"
    }, {
      label: "75%",
      value: "75%"
    }, {
      label: "100%",
      value: "100%"
    }];
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== onUpdate) {
    t11 = v => onUpdate({
      width: v
    });
    $[13] = onUpdate;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11 || $[16] !== t9) {
    t12 = <SelectControl label="Width" value={t9} options={t10} onChange={t11} />;
    $[15] = t11;
    $[16] = t9;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = attrs.alignment as string || "center";
  let t14;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [{
      label: "Left",
      value: "left"
    }, {
      label: "Center",
      value: "center"
    }, {
      label: "Right",
      value: "right"
    }];
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== onUpdate) {
    t15 = v_0 => onUpdate({
      alignment: v_0
    });
    $[19] = onUpdate;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== t13 || $[22] !== t15) {
    t16 = <SelectControl label="Alignment" value={t13} options={t14} onChange={t15} />;
    $[21] = t13;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t12 || $[25] !== t16 || $[26] !== t4 || $[27] !== t8) {
    t17 = <Section title="Image">{t4}{t8}{t12}{t16}</Section>;
    $[24] = t12;
    $[25] = t16;
    $[26] = t4;
    $[27] = t8;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  return t17;
}
function NumberControl(t0) {
  const $ = _c(11);
  const {
    label,
    value,
    onChange,
    placeholder
  } = t0;
  const numericValue = parseInt(value, 10);
  let t1;
  if ($[0] !== label) {
    t1 = <Label className="text-caption">{label}</Label>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isNaN(numericValue) ? "" : numericValue;
  let t3;
  if ($[2] !== onChange) {
    t3 = e => {
      const n = parseInt(e.target.value, 10);
      onChange(isNaN(n) || n < 0 ? "0px" : `${n}px`);
    };
    $[2] = onChange;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = placeholder ?? "0";
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = <Input type="number" min={0} value={t2} onChange={t3} className="h-8 text-caption" placeholder={t4} />;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t1 || $[9] !== t5) {
    t6 = <div className="space-y-1">{t1}{t5}</div>;
    $[8] = t1;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function ButtonControls(t0) {
  const $ = _c(87);
  const {
    attrs,
    onUpdate
  } = t0;
  let t1;
  if ($[0] !== attrs || $[1] !== onUpdate) {
    t1 = (property, value, cssProperty) => onUpdate(writeStyle(attrs, property, value, {
      target: "named",
      cssProperty
    }));
    $[0] = attrs;
    $[1] = onUpdate;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const setStyle = t1;
  let t2;
  if ($[3] !== attrs) {
    t2 = readStyle(attrs, "backgroundColor") || "#006bb5";
    $[3] = attrs;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== setStyle) {
    t3 = v => setStyle("backgroundColor", v);
    $[5] = setStyle;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <ColorControl label="Background color" value={t2} onChange={t3} />;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== attrs) {
    t5 = readStyle(attrs, "textColor", "color") || "#ffffff";
    $[10] = attrs;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== setStyle) {
    t6 = v_0 => setStyle("textColor", v_0, "color");
    $[12] = setStyle;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <ColorControl label="Text color" value={t5} onChange={t6} />;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== attrs) {
    t8 = readStyle(attrs, "borderRadius") || "12px";
    $[17] = attrs;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== setStyle) {
    t9 = v_1 => setStyle("borderRadius", v_1);
    $[19] = setStyle;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <NumberControl label="Border radius (px)" value={t8} onChange={t9} />;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== attrs) {
    t11 = readStyle(attrs, "paddingHorizontal", "padding-left") || "24px";
    $[24] = attrs;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== setStyle) {
    t12 = v_2 => setStyle("paddingHorizontal", v_2, "padding-left");
    $[26] = setStyle;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t11 || $[29] !== t12) {
    t13 = <NumberControl label="Horizontal padding (px)" value={t11} onChange={t12} />;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== attrs) {
    t14 = readStyle(attrs, "paddingVertical", "padding-top") || "8px";
    $[31] = attrs;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== setStyle) {
    t15 = v_3 => setStyle("paddingVertical", v_3, "padding-top");
    $[33] = setStyle;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t14 || $[36] !== t15) {
    t16 = <NumberControl label="Vertical padding (px)" value={t14} onChange={t15} />;
    $[35] = t14;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t10 || $[39] !== t13 || $[40] !== t16 || $[41] !== t4 || $[42] !== t7) {
    t17 = <Section title="Button">{t4}{t7}{t10}{t13}{t16}</Section>;
    $[38] = t10;
    $[39] = t13;
    $[40] = t16;
    $[41] = t4;
    $[42] = t7;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== attrs) {
    t18 = readStyle(attrs, "borderWidth") || "0px";
    $[44] = attrs;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== setStyle) {
    t19 = v_4 => setStyle("borderWidth", v_4);
    $[46] = setStyle;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== t18 || $[49] !== t19) {
    t20 = <NumberControl label="Border width (px)" value={t18} onChange={t19} />;
    $[48] = t18;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== attrs) {
    t21 = readStyle(attrs, "borderStyle") || "solid";
    $[51] = attrs;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== setStyle) {
    t22 = v_5 => setStyle("borderStyle", v_5);
    $[53] = setStyle;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== t21 || $[56] !== t22) {
    t23 = <SelectControl label="Border style" value={t21} options={BORDER_STYLE_OPTIONS} onChange={t22} />;
    $[55] = t21;
    $[56] = t22;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== attrs) {
    t24 = readStyle(attrs, "borderColor") || "#000000";
    $[58] = attrs;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[60] !== setStyle) {
    t25 = v_6 => setStyle("borderColor", v_6);
    $[60] = setStyle;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== t24 || $[63] !== t25) {
    t26 = <ColorControl label="Border color" value={t24} onChange={t25} />;
    $[62] = t24;
    $[63] = t25;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== t20 || $[66] !== t23 || $[67] !== t26) {
    t27 = <Section title="Border">{t20}{t23}{t26}</Section>;
    $[65] = t20;
    $[66] = t23;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  const t28 = attrs.alignment as string || "center";
  let t29;
  if ($[69] !== onUpdate) {
    t29 = v_7 => onUpdate({
      alignment: v_7
    });
    $[69] = onUpdate;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== t28 || $[72] !== t29) {
    t30 = <SelectControl label="Alignment" value={t28} options={TEXT_ALIGN_OPTIONS} onChange={t29} />;
    $[71] = t28;
    $[72] = t29;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Label className="text-caption">URL</Label>;
    $[74] = t31;
  } else {
    t31 = $[74];
  }
  const t32 = attrs.href as string || "";
  let t33;
  if ($[75] !== onUpdate) {
    t33 = e => onUpdate({
      href: e.target.value
    });
    $[75] = onUpdate;
    $[76] = t33;
  } else {
    t33 = $[76];
  }
  let t34;
  if ($[77] !== t32 || $[78] !== t33) {
    t34 = <div className="space-y-1">{t31}<Input value={t32} onChange={t33} className="h-8 text-caption" placeholder="https://..." /></div>;
    $[77] = t32;
    $[78] = t33;
    $[79] = t34;
  } else {
    t34 = $[79];
  }
  let t35;
  if ($[80] !== t30 || $[81] !== t34) {
    t35 = <Section title="Layout">{t30}{t34}</Section>;
    $[80] = t30;
    $[81] = t34;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  let t36;
  if ($[83] !== t17 || $[84] !== t27 || $[85] !== t35) {
    t36 = <>{t17}{t27}{t35}</>;
    $[83] = t17;
    $[84] = t27;
    $[85] = t35;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  return t36;
}
function BlockquoteControls(t0) {
  const $ = _c(27);
  const {
    attrs,
    onUpdate
  } = t0;
  let t1;
  if ($[0] !== attrs) {
    t1 = readStyle(attrs, "borderColor", "border-left-color") || "#006bb5";
    $[0] = attrs;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const borderColor = t1;
  let t2;
  if ($[2] !== attrs || $[3] !== onUpdate) {
    t2 = value => onUpdate(writeStyle(attrs, "borderColor", value, {
      target: "named",
      cssProperty: "border-left-color"
    }));
    $[2] = attrs;
    $[3] = onUpdate;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const setBorderColor = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Label className="text-caption">Border color</Label>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== setBorderColor) {
    t4 = e => setBorderColor(e.target.value);
    $[6] = setBorderColor;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== borderColor || $[9] !== t4) {
    t5 = <input type="color" value={borderColor} onChange={t4} className="size-8 shrink-0 cursor-pointer rounded border border-border bg-transparent" />;
    $[8] = borderColor;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== setBorderColor) {
    t6 = e_0 => setBorderColor(e_0.target.value);
    $[11] = setBorderColor;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== borderColor || $[14] !== t6) {
    t7 = <Input value={borderColor} onChange={t6} className="h-8 font-mono text-caption" />;
    $[13] = borderColor;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t5 || $[17] !== t7) {
    t8 = <div className="space-y-1">{t3}<div className="flex items-center gap-2">{t5}{t7}</div></div>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = attrs.textAlign as string || "left";
  let t10;
  if ($[19] !== onUpdate) {
    t10 = v => onUpdate({
      textAlign: v
    });
    $[19] = onUpdate;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t10 || $[22] !== t9) {
    t11 = <SelectControl label="Text align" value={t9} options={TEXT_ALIGN_OPTIONS} onChange={t10} />;
    $[21] = t10;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t8) {
    t12 = <Section title="Blockquote">{t8}{t11}</Section>;
    $[24] = t11;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}
const BLOCK_CONTROLS: Record<string, React.ComponentType<{
  attrs: Record<string, unknown>;
  onUpdate: (patch: Record<string, unknown>) => void;
}>> = {
  emailParagraph: ParagraphControls,
  emailHeading: HeadingControls,
  emailImage: ImageControls,
  emailButton: ButtonControls,
  emailBlockquote: BlockquoteControls
};

/* ── Main panel ───────────────────────────────────────────────────────── */

export interface BlockPropertiesPanelProps {
  activeBlock: ActiveBlock;
  editor: Editor;
  blockRegistry: Record<string, EmailBlockDefinition>;
  onClose: () => void;
}
export function BlockPropertiesPanel(t0) {
  const $ = _c(29);
  const {
    activeBlock,
    editor,
    onClose
  } = t0;
  const {
    pos,
    definition
  } = activeBlock;
  let t1;
  if ($[0] !== pos) {
    t1 = ctx => {
      const node = ctx.editor.state.doc.nodeAt(pos);
      return (node?.attrs ?? {}) as Record<string, unknown>;
    };
    $[0] = pos;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== editor || $[3] !== t1) {
    t2 = {
      editor,
      selector: t1
    };
    $[2] = editor;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const attrs = useEditorState(t2);
  let t3;
  if ($[5] !== editor.state || $[6] !== editor.view || $[7] !== pos) {
    t3 = patch => {
      const {
        tr
      } = editor.state;
      const current = editor.state.doc.nodeAt(pos);
      if (current) {
        tr.setNodeMarkup(pos, undefined, {
          ...current.attrs,
          ...patch
        });
        editor.view.dispatch(tr);
      }
    };
    $[5] = editor.state;
    $[6] = editor.view;
    $[7] = pos;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handleUpdate = t3;
  const Controls = BLOCK_CONTROLS[definition.name];
  const Icon = definition.icon;
  let t4;
  if ($[9] !== Icon) {
    t4 = typeof Icon === "function" ? React.createElement(Icon, {
      className: "size-4"
    }) : Icon;
    $[9] = Icon;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t4) {
    t5 = <span className="text-foreground-secondary">{t4}</span>;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== definition.label) {
    t6 = <h2 className="flex-1 text-body-emphasis">{definition.label}</h2>;
    $[13] = definition.label;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <LuX className="size-4" />;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== onClose) {
    t8 = <button type="button" onClick={onClose} className="rounded p-0.5 text-foreground-secondary hover:bg-hover hover:text-foreground">{t7}</button>;
    $[16] = onClose;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t5 || $[19] !== t6 || $[20] !== t8) {
    t9 = <div className="flex items-center gap-2 border-b border-border px-4 py-3">{t5}{t6}{t8}</div>;
    $[18] = t5;
    $[19] = t6;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== Controls || $[23] !== attrs || $[24] !== handleUpdate) {
    t10 = Controls ? <Controls attrs={attrs} onUpdate={handleUpdate} /> : <div className="px-4 py-6 text-center text-caption text-foreground-muted">No editable properties for this block.</div>;
    $[22] = Controls;
    $[23] = attrs;
    $[24] = handleUpdate;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <div className="text-foreground">{t9}{t10}</div>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  return t11;
}